<script setup lang="ts">
// Thread chat assistant — partage entre AssistantDrawer (desktop Cmd+J)
// et AssistantPage (mobile full-screen). P8.H.

import { nextTick, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useChatStore } from "@/features/assistant/stores/chat-store";
import PlanCard from "@/widgets/PlanCard.vue";

const props = defineProps<{ accountId: string }>();

const store = useChatStore();
const { messages, pending, pendingPlan, thinking, error } = storeToRefs(store);
const threadEl = ref<HTMLDivElement | null>(null);

watch(
  [messages, pending],
  () => {
    void nextTick(() => {
      if (threadEl.value) threadEl.value.scrollTop = threadEl.value.scrollHeight;
    });
  },
  { deep: true },
);
</script>

<template>
  <div ref="threadEl" class="thread" role="log" aria-live="polite">
    <p v-if="error" class="error">{{ error }}</p>
    <div v-for="m in messages" :key="m.id" :class="['bubble', m.role]">
      {{ m.content }}
    </div>
    <div v-if="pending !== null" class="bubble assistant">
      <span v-if="pending?.content">{{ pending.content }}</span>
      <span v-else class="thinking" aria-busy="true">···</span>
    </div>
    <PlanCard
      v-if="pendingPlan"
      :plan="pendingPlan.plan_json"
      :loading="thinking"
      @approve="store.approveAndExecute(props.accountId)"
      @reject="store.rejectPlan()"
    />
  </div>
</template>

<style scoped>
.thread {
  padding: 1rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1 1 auto;
}
.bubble {
  padding: 0.6rem 0.85rem;
  border-radius: 8px;
  font-size: 0.9rem;
  line-height: 1.4;
  white-space: pre-wrap;
  max-width: 90%;
}
.bubble.user {
  align-self: flex-end;
  background: rgba(232, 160, 32, 0.15);
  color: var(--color-cream);
}
.bubble.assistant,
.bubble.tool {
  align-self: flex-start;
  background: var(--color-navy-light);
  color: var(--color-cream);
}
.thinking {
  display: inline-block;
  animation: pulse 1.2s infinite;
  color: var(--color-or);
  font-size: 1.2rem;
  letter-spacing: 0.2em;
}
@keyframes pulse {
  0%,
  100% {
    opacity: 0.4;
  }
  50% {
    opacity: 1;
  }
}
.error {
  color: var(--color-danger);
  background: rgba(184, 67, 74, 0.1);
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-size: 0.85rem;
}
</style>
